import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@shared/ui/Button";
import { useForm } from "@shared/features/form/useForm";
import { makeFormFields } from "@shared/utils/makeFormFields";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { baseResetPasswordSchema } from "../../auth.schemas";
import useResetPasswordMutation from "../../hooks/useResetPasswordMutation";
import AuthContent from "../AuthContent/AuthContent";
import { getConfig } from "@shared/services/api/config";

type Props = {
  onCancel: () => void;
  onError?: () => void;
  onSuccess?: (data: any) => void;
  oobCode?: string;
};

export function ResetPasswordContainer({
  onCancel,
  onSuccess,
  onError,
  oobCode,
}: Props) {
  const [isSuccess, setIsSuccess] = useState(false);

  const schema = baseResetPasswordSchema;

  const fields = makeFormFields(["password", "confirmPassword"]);

  const forgotPasswordMutation = useResetPasswordMutation({
    onSuccess: (data: any) => {
      setIsSuccess(true);
      onSuccess?.(data);
    },
    onError,
    schema,
    oobCode: oobCode || "",
  });

  // Redirect to pollinator.coop after successful password reset in portal app
  // TODO: This is a temporary solution to redirect to pollinator.coop after successful password reset in portal app
  // We need to find a better way to handle this
  useEffect(() => {
    if (isSuccess) {
      try {
        const config = getConfig();
        if (config.appName === "portal") {
          // Small delay to show success message before redirect
          const timer = setTimeout(() => {
            window.location.href = "https://pollinator.coop";
          }, 2000);
          return () => clearTimeout(timer);
        }
      } catch (error) {
        // Config not set yet, ignore
      }
    }
  }, [isSuccess]);

  const { form, handleSubmit } = useForm({
    schema,
    onSubmit: forgotPasswordMutation.mutateAsync,
  });

  const { isSubmitting, isLoading, isValid } = form.formState;

  // Check if we're in portal app to conditionally show the back button
  const isPortalApp = (() => {
    try {
      const config = getConfig();
      return config.appName === "portal";
    } catch (error) {
      return false;
    }
  })();

  return (
    <AuthContent
      title={isSuccess ? "Password Reset" : "Set New Password"}
      description={
        isSuccess
          ? "Your password has been successfully reset. Redirecting to Pollinator..."
          : "Your new password must be different from previously used passwords."
      }
    >
      {isSuccess ? (
        <></>
      ) : (
        <>
          <ResetPasswordForm fields={fields} form={form} />
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !isValid}
            type="submit"
          >
            Reset Password
          </Button>
        </>
      )}
      {!isPortalApp && (
        <Button variant="link" iconComponent={ArrowLeft} onClick={onCancel}>
          Back to Login
        </Button>
      )}
    </AuthContent>
  );
}

export default ResetPasswordContainer;
